import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Menu,
  MenuItem,
  Popover,
  Toolbar,
  Typography,
  Button,
  Container,
  Badge,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import api from '../api/client';
import { useBranding } from '../theme/BrandColorContext';
import { useAuth } from '../context/AuthContext';
import { useTenantPath } from '../context/TenantPathContext';
import IntegrationHealthCard from './domain/IntegrationHealthCard';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import FlagIcon from '@mui/icons-material/OutlinedFlag';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import BusinessIcon from '@mui/icons-material/Business';

const DRAWER_WIDTH = 264;
const IMPERSONATION_BAR_HEIGHT = 44;

const BASE_NAV_ITEMS = [
  { label: 'Kampanie', path: '/campaigns', icon: <FlagIcon /> },
  { label: 'Szablony', path: '/campaigns/templates', icon: <DescriptionOutlinedIcon /> },
  { label: 'Pracownicy', path: '/employees', icon: <GroupOutlinedIcon /> },
  { label: 'Integracje', path: '/integrations', icon: <ExtensionOutlinedIcon /> },
  { label: 'Raporty', path: '/reports', icon: <InsertChartOutlinedIcon /> },
  { label: 'Ustawienia', path: '/settings', icon: <SettingsOutlinedIcon /> },
];

const CONSOLE_NAV_ITEM = {
  label: 'Zarządzanie klientami',
  path: '/console/clients',
  icon: <BusinessIcon />,
};

const NOTIFICATIONS = [
  {
    id: 1,
    title: 'Kampania "10M kroków" osiągnęła 75% celu',
    time: '10 min temu',
    type: 'success',
    read: false,
  },
  { id: 2, title: 'Integracja SCIM wymaga uwagi', time: '1h temu', type: 'warning', read: false },
  {
    id: 3,
    title: '5 nowych pracowników dołączyło do wyzwania',
    time: '2h temu',
    type: 'info',
    read: false,
  },
  { id: 4, title: 'Kampania "Bieg firmowy" rozpoczęta', time: 'Wczoraj', type: 'info', read: true },
  {
    id: 5,
    title: 'Raport tygodniowy gotowy do pobrania',
    time: 'Wczoraj',
    type: 'info',
    read: true,
  },
];

const NOTIFICATION_ICON = {
  success: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
  info: <CampaignOutlinedIcon fontSize="small" color="primary" />,
};

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const tokens = theme.tokens?.semantic;
  const { branding } = useBranding();
  const { user, logout, isPlatformAdmin, exitTenant, activeTenantId } = useAuth();
  const { prefix } = useTenantPath();
  const [tenantName, setTenantName] = useState('Challenges');
  const [integrations, setIntegrations] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState(NOTIFICATIONS);
  const [notifLoading, setNotifLoading] = useState(false);

  const navItems = isPlatformAdmin ? [...BASE_NAV_ITEMS, CONSOLE_NAV_ITEM] : BASE_NAV_ITEMS;

  useEffect(() => {
    if (!activeTenantId) {
      setTenantName('Challenges');
      return;
    }
    api
      .get('/api/tenants/me')
      .then((tenant) => setTenantName(tenant.name || 'Challenges'))
      .catch(() => {});
  }, [activeTenantId]);

  useEffect(() => {
    if (!activeTenantId) {
      setIntegrations([]);
      return;
    }
    api
      .get('/api/tenants/me/integrations')
      .then((data) => setIntegrations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [activeTenantId]);

  const integrationsSummary = integrations.map((i) => ({
    name: i.name,
    status: i.status === 'ok' ? 'Działa' : i.status,
    ok: i.status === 'ok',
  }));
  const lastSyncRaw =
    integrations.find((i) => i.last_sync_at)?.last_sync_at || integrations[0]?.lastSync || null;
  const lastSync = (() => {
    if (!lastSyncRaw) return '—';
    const d = new Date(lastSyncRaw);
    return isNaN(d.getTime())
      ? lastSyncRaw
      : d.toLocaleString('pl-PL', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
  })();

  useEffect(() => {
    api
      .get('/api/notifications')
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setNotifications(
            data.map((n) => ({
              id: n.id,
              title: n.title,
              time: n.created_at ? new Date(n.created_at).toLocaleString('pl-PL') : '—',
              type: n.type || 'info',
              read: n.read === 1 || n.read === true,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleMarkAllRead = async () => {
    setNotifLoading(true);
    try {
      await Promise.all(
        notifications.filter((n) => !n.read).map((n) => api.put(`/api/notifications/${n.id}/read`))
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error(err);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const isActive = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 3, gap: 1.5 }}>
        <Box
          component="img"
          src={branding.logo || '/logo.png'}
          alt="Logo"
          sx={{
            width: '100%',
            height: 'auto',
            maxHeight: 56,
            borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
            objectFit: 'contain',
          }}
        />
      </Toolbar>
      <List sx={{ px: 2, pt: 1, flexGrow: 1 }}>
        {navItems.map((item) => {
          const to = item.path.startsWith('/console') ? item.path : `${prefix}${item.path}`;
          const active = isActive(to);
          return (
            <ListItem key={to} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={to}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: `${theme.tokens?.primitive?.radius?.control ?? 12}px`,
                  position: 'relative',
                  bgcolor: active ? tokens?.surface.brandTint : 'transparent',
                  color: active ? 'primary.main' : 'text.secondary',
                  '&:hover': {
                    bgcolor: active ? tokens?.surface.brandTint : 'action.hover',
                  },
                  '&::before': active
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 4,
                        height: 22,
                        borderRadius: '0 4px 4px 0',
                        bgcolor: 'primary.main',
                      }
                    : {},
                }}
              >
                <ListItemIcon
                  sx={{ color: active ? 'primary.main' : 'text.secondary', minWidth: 36 }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: active ? 650 : 500, fontSize: '0.9rem' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {activeTenantId && (
        <Box sx={{ px: 2, pb: 1 }}>
          <IntegrationHealthCard
            overallOk={integrationsSummary.length === 0 || integrationsSummary.every((i) => i.ok)}
            lastSyncLabel={`Synchronizacja: ${lastSync}`}
            integrations={
              integrationsSummary.length > 0
                ? integrationsSummary
                : [{ name: 'Brak integracji', status: '—', ok: true }]
            }
            onManage={() => navigate(`${prefix}/integrations`)}
          />
        </Box>
      )}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            bgcolor: tokens?.surface.brandTint,
            p: 2,
          }}
        >
          <Typography variant="body2" fontWeight={650} gutterBottom>
            {branding.slogan || 'Potrzebujesz pomocy?'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {branding.slogan ? 'Twój branding platformy' : 'Skontaktuj się z nami'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      {isPlatformAdmin && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.drawer + 2,
            height: IMPERSONATION_BAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            Jesteś w kontekście klienta: {tenantName}
          </Typography>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={exitTenant}
            sx={{
              color: 'inherit',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': { borderColor: 'primary.contrastText', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
            variant="outlined"
          >
            Wróć do konsoli operatora
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <AppBar
          elevation={0}
          position="fixed"
          color="inherit"
          sx={{
            top: isPlatformAdmin ? IMPERSONATION_BAR_HEIGHT : 0,
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { md: `${DRAWER_WIDTH}px` },
            bgcolor: 'background.default',
            borderBottom: `1px solid`,
            borderColor: 'divider',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
            {mobileSearchOpen ? (
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    px: 2,
                    py: 0.5,
                    flex: 1,
                  }}
                >
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
                  <InputBase
                    autoFocus
                    placeholder="Szukaj..."
                    fullWidth
                    sx={{ fontSize: '0.9375rem' }}
                    inputProps={{ 'aria-label': 'search' }}
                  />
                </Box>
                <IconButton
                  aria-label="zamknij wyszukiwanie"
                  onClick={() => setMobileSearchOpen(false)}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <IconButton
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 1, display: { md: 'none' } }}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      px: 2,
                      py: 0.5,
                      width: 320,
                    }}
                  >
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
                    <InputBase
                      placeholder="Szukaj..."
                      sx={{ fontSize: '0.9375rem' }}
                      inputProps={{ 'aria-label': 'search' }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                  <IconButton
                    aria-label="szukaj"
                    onClick={() => setMobileSearchOpen(true)}
                    sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                  >
                    <SearchIcon />
                  </IconButton>
                  <Tooltip title="Powiadomienia">
                    <IconButton
                      aria-label="notifications"
                      onClick={(e) => setNotifAnchorEl(e.currentTarget)}
                    >
                      <Badge badgeContent={unreadCount} color="error">
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Konto">
                    <IconButton
                      onClick={(e) => setAccountAnchorEl(e.currentTarget)}
                      sx={{ p: 0.5 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          width: 36,
                          height: 36,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                        }}
                      >
                        PK
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
          aria-label="main navigation"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                top: isPlatformAdmin ? IMPERSONATION_BAR_HEIGHT : 0,
                height: isPlatformAdmin ? `calc(100% - ${IMPERSONATION_BAR_HEIGHT}px)` : '100%',
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                top: isPlatformAdmin ? IMPERSONATION_BAR_HEIGHT : 0,
                height: isPlatformAdmin ? `calc(100% - ${IMPERSONATION_BAR_HEIGHT}px)` : '100%',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3, md: 4 },
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            minHeight: '100vh',
            bgcolor: 'background.default',
            overflowX: 'hidden',
          }}
        >
          {isPlatformAdmin && <Box sx={{ height: IMPERSONATION_BAR_HEIGHT }} />}
          <Toolbar />
          <Container maxWidth="xl" disableGutters>
            <Outlet />
          </Container>
        </Box>
      </Box>

      {/* Account Menu */}
      <Menu
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={() => setAccountAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            boxShadow: theme.tokens?.semantic?.shadow?.float,
          },
        }}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {user?.displayName || user?.email || 'Użytkownik'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountAnchorEl(null);
            navigate(`${prefix}/settings`);
          }}
        >
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Moje konto</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAccountAnchorEl(null);
            navigate(`${prefix}/settings`);
          }}
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Ustawienia</ListItemText>
        </MenuItem>
        {isPlatformAdmin && (
          <MenuItem
            onClick={() => {
              setAccountAnchorEl(null);
              exitTenant();
            }}
          >
            <ListItemIcon>
              <ArrowBackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>
              Wróć do konsoli operatora
            </ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountAnchorEl(null);
            logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', color: 'error.main' }}>
            Wyloguj się
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications Popover */}
      <Popover
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={() => setNotifAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 320, sm: 380 },
            maxHeight: 440,
            borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
            boxShadow: theme.tokens?.semantic?.shadow?.float,
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontFamily: theme.typography.h1.fontFamily }}
          >
            Powiadomienia
          </Typography>
          <Chip label={`${unreadCount} nowe`} size="small" color="primary" variant="outlined" />
        </Box>
        <Divider />
        <List disablePadding sx={{ maxHeight: 340, overflowY: 'auto' }}>
          {notifications.map((notif) => (
            <ListItem
              key={notif.id}
              disablePadding
              sx={{
                bgcolor: notif.read
                  ? 'transparent'
                  : (tokens?.surface?.brandTint ?? 'action.hover'),
              }}
            >
              <ListItemButton
                sx={{ py: 1.5, px: 2.5, gap: 1.5 }}
                onClick={() => setNotifAnchorEl(null)}
              >
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: notif.read ? 'action.selected' : tokens?.surface?.brandTint,
                    }}
                  >
                    {NOTIFICATION_ICON[notif.type]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={notif.title}
                  secondary={notif.time}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: notif.read ? 450 : 650,
                    lineHeight: 1.4,
                  }}
                  secondaryTypographyProps={{ fontSize: '0.6875rem' }}
                />
                {!notif.read && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={handleMarkAllRead}
          >
            {notifLoading ? <CircularProgress size={14} /> : 'Oznacz wszystkie jako przeczytane'}
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}

export default Layout;
